// The onTrigger factory for a committed sample. Called once per
// registered sound; returns a handler that superdough invokes for
// every pattern hit.
//
// Shape follows superdough's SoundHandler contract: each trigger
// spins a fresh AudioBufferSourceNode (they're single-use), wires a
// per-voice gain for amplitude control, honours speed / begin / end /
// loop / gain / note from the hap value, and returns
// { node, nodes, stop } so superdough can schedule release and tracking.
//
// The returned handler intentionally connects only the outer gain to
// the graph — superdough takes that `node` and patches it into the FX
// chain + destination, which also trips the harness's recording tap
// via the connect-patch in player.ts. We don't reach for the
// destination directly here.

import type { HapValue, SoundHandlerResult } from '../strudel/types';

export type PlaybackOptions = {
  /** When set, note / n in the hap value transposes playback rate so
   *  the sample plays at the requested MIDI pitch. Untuned samples
   *  (drums) leave this undefined and speed() is the only pitch knob. */
  pitchedRootMidi?: number;
};

// Narrower than SoundHandler: this factory always returns synchronously.
// The broader superdough SoundHandler type permits Promise returns, but
// committing that to sample playback would force every consumer to await.
export type SamplePlayback = (
  t: number,
  value: HapValue,
  onEnded: () => void,
  cps: number,
) => SoundHandlerResult | undefined;

export function createSamplePlayback(
  ctx: AudioContext,
  buffer: AudioBuffer,
  opts: PlaybackOptions = {},
): SamplePlayback {
  return (t, value, onEnded) => {
    const speed = numberOf(value.speed, 1);
    // superdough convention: return undefined to signal a silent voice.
    // This is how `speed(0)` is meant to behave.
    if (speed === 0) return undefined;

    const gainValue = numberOf(value.gain, 1);
    const begin = clampPositive(numberOf(value.begin, 0));
    const endRaw = numberOf(value.end, buffer.duration);
    const end = Math.max(begin, Math.min(buffer.duration, endRaw));
    const loop = booleanOf(value.loop, false);
    const rate = speed * pitchRate(value, opts.pitchedRootMidi);

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    if (loop) {
      src.loop = true;
      src.loopStart = begin;
      src.loopEnd = end;
    }

    const gain = ctx.createGain();
    gain.gain.value = gainValue;
    src.connect(gain);

    // Start at t with `begin` as the offset into the buffer. If the slice
    // length is finite and loop is off, schedule a natural stop so the
    // voice releases exactly when it should.
    const sliceDuration = end - begin;
    src.start(t, begin);
    if (!loop && sliceDuration > 0 && sliceDuration < buffer.duration) {
      src.stop(t + sliceDuration / Math.max(Math.abs(rate), 1e-6));
    }

    let ended = false;
    src.onended = () => {
      if (ended) return;
      ended = true;
      onEnded();
    };

    const result: SoundHandlerResult = {
      node: gain,
      nodes: { source: [src] },
      stop: (endTime) => {
        try {
          src.stop(endTime);
        } catch {
          // Source may already be stopped (natural end, double-stop).
          // Either way, harmless.
        }
      },
    };
    return result;
  };
}

function numberOf(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function booleanOf(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  return fallback;
}

function clampPositive(n: number): number {
  return n > 0 ? n : 0;
}

/** Fire-and-forget one-shot playback of an AudioBuffer. Spins a fresh
 *  source through a per-call gain node and connects to `ctx.destination`
 *  directly — bypasses the Strudel/superdough graph because audition
 *  doesn't need pattern scheduling, FX, or recording-tap participation.
 *  Returns a stop function so callers can cut the voice early
 *  (subsequent click on the same row, component unmount). */
export function playSampleOnce(
  ctx: AudioContext,
  buffer: AudioBuffer,
  opts: { gain?: number } = {},
): () => void {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = opts.gain ?? 0.85;
  src.connect(gain);
  gain.connect(ctx.destination);
  src.start();
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    try {
      src.stop();
    } catch {
      // Already ended naturally — harmless.
    }
  };
}

// Pitched rate derived from note / n and a root midi. For untuned
// samples this is always 1.
function pitchRate(value: HapValue, root: number | undefined): number {
  if (root === undefined) return 1;
  const requested =
    typeof value.note === 'number'
      ? value.note
      : typeof value.n === 'number'
        ? value.n
        : undefined;
  if (requested === undefined) return 1;
  return Math.pow(2, (requested - root) / 12);
}
